import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import AddEditBrandModal from './AddEditBrandModal';
import BrandCataloguesViewerModal from './BrandCataloguesViewerModal';
import { getThumbnailUrl } from '../../utils/api';
import './Salon.css';

const ManageBrands = ({ salonId, userRole, currentPage: urlPage = 1, onPageChange }) => {
  const { t } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(urlPage);
  const [totalPages, setTotalPages] = useState(1);
  const [sortField, setSortField] = useState('brand_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewingBrand, setViewingBrand] = useState(null);
  const [isViewerModalOpen, setIsViewerModalOpen] = useState(false);

  const itemsPerPage = 10;

  // Sync URL page with local state
  useEffect(() => {
    setCurrentPage(urlPage);
  }, [urlPage]);

  const fetchBrands = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch brands and their statistics
      const [brandsResponse, dressesResponse, reservationsResponse] = await Promise.all([
        axios.get(`/api/salons/${salonId}/dress-brands`, {
          headers: { 'Authorization': `Bearer ${token}` },
          params: {
            page: currentPage,
            limit: itemsPerPage,
            sortField: sortField,
            sortDirection: sortDirection
          }
        }),
        axios.get(`/api/salons/${salonId}/dresses?limit=1000000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`/api/salons/${salonId}/reservations?limit=1000000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      // Calculate statistics for each brand
      let brandsWithStats = brandsResponse.data.brands.map(brand => {
        const dressesCount = dressesResponse.data.dresses.filter(dress => dress.brand_id === brand.id).length;
        const reservationsCount = reservationsResponse.data.reservations.filter(reservation => 
          dressesResponse.data.dresses.some(dress => dress.id === reservation.dress_id && dress.brand_id === brand.id)
        ).length;

        return {
          ...brand,
          dresses_count: dressesCount,
          reservations_count: reservationsCount,
          reservations_per_dress: dressesCount > 0 ? Math.round((reservationsCount / dressesCount) * 10) / 10 : 0
        };
      });

      // Client-side sorting for calculated fields
      if (['dresses_count', 'reservations_count', 'reservations_per_dress'].includes(sortField)) {
        brandsWithStats.sort((a, b) => {
          const aValue = a[sortField] || 0;
          const bValue = b[sortField] || 0;
          
          if (sortDirection === 'asc') {
            return aValue - bValue;
          } else {
            return bValue - aValue;
          }
        });
      }

      setBrands(brandsWithStats);
      setTotalPages(brandsResponse.data.pagination.totalPages);
      setError('');
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Failed to fetch brands');
      }
    } finally {
      setLoading(false);
    }
  }, [salonId, currentPage, sortField, sortDirection]);

  useEffect(() => {
    fetchBrands();
  }, [fetchBrands]);

  const handleAddBrand = () => {
    setIsAddModalOpen(true);
  };

  const handleEditBrand = (brand) => {
    setEditingBrand(brand);
    setIsEditModalOpen(true);
  };

  const handleViewCatalogues = (brand) => {
    setViewingBrand(brand);
    setIsViewerModalOpen(true);
  };

  const handleCloseViewerModal = () => {
    setIsViewerModalOpen(false);
    setViewingBrand(null);
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm(t('messages.confirm.deleteBrand'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/dress-brands/${brandId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Refresh brands list
      fetchBrands();
    } catch (error) {
      if (error.response?.data?.error) {
        alert(error.response.data.error);
      } else {
        alert('Failed to delete brand');
      }
    }
  };

  const handleBrandAdded = () => {
    setIsAddModalOpen(false);
    fetchBrands();
  };

  const handleBrandUpdated = () => {
    setIsEditModalOpen(false);
    setEditingBrand(null);
    fetchBrands();
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingBrand(null);
  };

  const handlePageChange = (newPage) => {
    if (onPageChange) {
      onPageChange(newPage);
    } else {
      setCurrentPage(newPage);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending for numeric columns, ascending for others
      setSortField(field);
      const defaultDirection = ['dresses_count', 'reservations_count', 'reservations_per_dress'].includes(field) ? 'desc' : 'asc';
      setSortDirection(defaultDirection);
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          className={`pagination-btn ${currentPage === i ? 'active' : ''}`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="pagination">
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t('common.previous')}
        </button>
        {pages}
        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('common.next')}
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="brands-loading">
        <div className="loading-spinner"></div>
        <p>{t('messages.loading.brands')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="brands-error">
        <p className="error-message">{error}</p>
        <button onClick={fetchBrands} className="salon-button secondary">
          {t('common.search')}
        </button>
      </div>
    );
  }

  return (
    <div className="manage-page">
      <div className="table-header">
        <h3>{t('brands.title')}</h3>
        {(userRole === 'owner' || userRole === 'admin') && (
          <button onClick={handleAddBrand} className="salon-button primary">
            {t('brands.addBrand')}
          </button>
        )}
      </div>

      {brands.length === 0 ? (
        <div className="table-empty-state">
          <div className="table-empty-state-icon">🏷️</div>
          <h4>{t('brands.noBrands')}</h4>
          <p>{t('brands.startPortfolio')}</p>
          {(userRole === 'owner' || userRole === 'admin') && (
            <button onClick={handleAddBrand} className="salon-button primary">
              {t('brands.addBrand')}
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th 
                    className="sortable fixed-column" 
                    onClick={() => handleSort('brand_name')}
                    title={`${t('common.tooltips.clickToSort')} ${t('brands.brandName').toLowerCase()}`}
                  >
                    {t('brands.brandName')}
                    {sortField === 'brand_name' && (
                      <span className="sort-indicator">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('country')}
                  title={`${t('common.tooltips.clickToSort')} ${t('brands.country').toLowerCase()}`}
                >
                  {t('brands.country')}
                  {sortField === 'country' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('dresses_count')}
                  title={`${t('common.tooltips.clickToSort')} ${t('brands.dressCount').toLowerCase()}`}
                >
                  {t('brands.dressCount')}
                  {sortField === 'dresses_count' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('reservations_count')}
                  title={`${t('common.tooltips.clickToSort')} ${t('dresses.reserveCount').toLowerCase()}`}
                >
                  {t('dresses.reserveCount')}
                  {sortField === 'reservations_count' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th 
                  className="sortable" 
                  onClick={() => handleSort('reservations_per_dress')}
                  title={`${t('common.tooltips.clickToSort')} ${t('brands.rpd').toLowerCase()}`}
                >
                  {t('brands.rpd')}
                  {sortField === 'reservations_per_dress' && (
                    <span className="sort-indicator">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand) => (
                <tr key={brand.id}>
                  <td className="fixed-column">
                    <div className="table-cell-content">
                      <div className="brand-info">
                      {brand.display_image && (
                        <img 
                          src={getThumbnailUrl(brand.display_image)}
                          alt={brand.brand_name}
                          className="table-preview-image"
                        />
                      )}
                        <div>
                          <div className="brand-name">{brand.brand_name}</div>
                          {brand.description && (
                            <div className="brand-description">{brand.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      {brand.country || '-'}
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="number-stat">{brand.dresses_count || 0}</div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="number-stat">{brand.reservations_count || 0}</div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="number-stat">{brand.reservations_per_dress || 0}</div>
                    </div>
                  </td>
                  <td>
                    <div className="table-cell-content">
                      <div className="table-actions">
                        <button
                          onClick={() => handleViewCatalogues(brand)}
                          className="action-button view"
                          title={t('brands.viewCatalogues')}
                        >
                          📄
                        </button>
                        {(userRole === 'owner' || userRole === 'admin') && (
                          <>
                            <button 
                              onClick={() => handleEditBrand(brand)}
                              className="action-button edit"
                              title={t('brands.editBrand')}
                            >
                              ✏️
                            </button>
                            <button 
                              onClick={() => handleDeleteBrand(brand.id)}
                              className="action-button delete"
                              title={t('brands.deleteBrand')}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="pagination-container">
        {renderPagination()}
      </div>

      {/* Add Brand Modal */}
      <AddEditBrandModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        salonId={salonId}
        onBrandAdded={handleBrandAdded}
        mode="add"
        userRole={userRole}
      />

      {/* Edit Brand Modal */}
      {editingBrand && (
        <AddEditBrandModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          salonId={salonId}
          brand={editingBrand}
          onBrandUpdated={handleBrandUpdated}
          mode="edit"
          userRole={userRole}
        />
      )}

      {viewingBrand && (
        <BrandCataloguesViewerModal
          isOpen={isViewerModalOpen}
          onClose={handleCloseViewerModal}
          salonId={salonId}
          brand={viewingBrand}
        />
      )}
    </div>
  );
};

export default ManageBrands;
